import * as React from 'react';
import { useState, useEffect } from 'react';
import Form from '../../../../common/form/Form';
import FormInput from '../../../../common/form/Input';
import { getGuestCard, GuestCard, updateGuestCard } from '../../../../../api/TourSignup';
import { updateProspectUserProfile } from '../../../../../api/ProspectUserProfile';
import { refreshJwt } from '../../../../../api/Shared';
import FormValidationDisplay from '../../../../common/form/ValidationDisplay';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import { Property } from '../../../../../api/Property';
import Checkbox from '../../../../common/form/Checkbox';
import * as moment from 'moment';
import { withSourceMediumAndCampaign } from '../../../../../utilities/sourceData';

const cameraIcon = require('../../../../../images/camera-icon.png');
const idIcon = require('../../../../../images/id-icon.png');
interface OnfidoBirthdayFormProps {
    updateOrCreateOnfidoApplication: (birthdate: string) => void;
    sdkToken?: string;
    guestCardGuid: string;
    propertyInfo: Property;
}

const OnfidoBirthdayForm = (props: OnfidoBirthdayFormProps) => {
    const [birthDateString, setBirthDateString] = useState<string | undefined>();
    const [birthdateValidated, setBirthdateValidated] = useState(false);

    const [firstName, setFirstName] = useState<string | undefined>();
    const [lastName, setLastName] = useState<string | undefined>();
    const [guestCard, setGuestCard] = useState<GuestCard | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);

    const termsPdf = require('../../../../../pdfs/Tour24_IDVerification_TermsofUse_2022.pdf');

    const handleFormSubmit = async () => {

        if (!birthDateString) {
            setErrorMessage('Please enter a valid date');
            return;
        }
        var date = moment(birthDateString, 'MM-DD-YYYY'); // MM-DD-YYYY is equivalent to MM/DD/YYYY
        if (
            !(
                date.isValid() &&
                date > moment('01/01/1900', 'MM-DD-YYYY') &&
                date.year() < moment().year() - 10
            )
        ) {
            setErrorMessage('Please enter a valid date');
            return;
        }

        // update name if it's been changed
        if (
            guestCard &&
            firstName &&
            lastName &&
            (firstName !== guestCard.profile.firstName || lastName !== guestCard.profile.lastName)
        ) {
            let profileResult = await updateProspectUserProfile({
                ...guestCard.profile,
                firstName: firstName,
                lastName: lastName
            });
            let guestCardResult = await updateGuestCard(
                guestCard.propertyId,
                guestCard.guestCardGuid,
                withSourceMediumAndCampaign(guestCard)
            );

            if (profileResult.success && guestCardResult.success) {
                await refreshJwt();
                setErrorMessage('');
            } else {
                setErrorMessage('Error updating name. Please try again.');
                return; // Don't submit date of birth if failed
            }
        }
        if (!termsChecked) {
            setErrorMessage('Please confirm that you agree to the ID policy.');
            return; // Don't submit if user hasn't accepted terms
        }

        var updateDateOfBirth = date.format('YYYY-MM-DD');
        props.updateOrCreateOnfidoApplication(updateDateOfBirth);
    };

    const loadGuestCard = async () => {
        setIsLoading(true);
        if (props.guestCardGuid) {
            const guestCardRequest = getGuestCard(props.propertyInfo.id, props.guestCardGuid);
            const guestCardResponse = await guestCardRequest;
            if (guestCardResponse.data) {
                setGuestCard(guestCardResponse.data);
                setFirstName(guestCardResponse.data.profile.firstName);
                setLastName(guestCardResponse.data.profile.lastName);
            }
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadGuestCard();
    }, []);

    return (
        <div>
            <LoadingOverlay show={isLoading} inBootstrapColumn={false} />
            <div className="birthday-form">
                <Form
                    onSubmit={handleFormSubmit}
                    submitButtonText={
                        props.sdkToken !== undefined
                            ? 'Update Date of Birth'
                            : 'Start ID Verification'
                    }
                    inBootstrapColumn={false}
                    wasFormValidated={birthdateValidated && termsChecked}
                    onFormValidated={() => setBirthdateValidated(true)}
                >
                    <FormInput
                        inputProps={{
                            id: 'first-name',
                            name: 'firstName',
                            value: firstName,
                            required: true
                        }}
                        labelProps={{
                            labelText: 'First Name on ID',
                            invalidText: 'Enter the same first name as on the ID'
                        }}
                        onChange={(inputName, newValue: string) => {
                            setFirstName(newValue);
                            setErrorMessage('');
                        }}
                    />
                    <FormInput
                        inputProps={{
                            id: 'last-name',
                            name: 'lastName',
                            value: lastName,
                            required: true
                        }}
                        labelProps={{
                            labelText: 'Last Name on ID',
                            invalidText: 'Enter the same last name as on the ID'
                        }}
                        onChange={(inputName, newValue: string) => {
                            setLastName(newValue);
                            setErrorMessage('');
                        }}
                    />
                     <FormInput
                        inputProps={{
                            id: 'birth-day',
                            name: 'birthDay',
                            placeholder: 'MM/DD/YYYY',
                            value: birthDateString || '',
                            required: true,
                            pattern: '[0-9]{2}/[0-9]{2}/[0-9]{4}',
                            mask: '99/99/9999'
                        }}
                        labelProps={{
                            labelText: 'Birth Date',
                            invalidText: 'Please enter a valid birth date'
                        }}
                        onChange={(inputName, newValue: string) => {
                            setBirthDateString(newValue.replace('_', '')); // remove underscores
                            setErrorMessage('');
                        }}
                    />
                    <FormValidationDisplay errorMessage={errorMessage} />

                    <div>We'll take a quick check of your photo ID and have you take a selfie.</div>
                    <div className="img-text-row">
                        <img src={idIcon} />
                        <span>Valid Photo ID</span>
                    </div>
                    <div className="img-text-row">
                        <img src={cameraIcon} />
                        <span>Selfie</span>
                    </div>
                    <br />
                    <Checkbox
                        inputProps={{
                            checked: termsChecked,
                            id: 'onfido-terms-checked',
                            name: 'termsChecked'
                        }}
                        onChange={() => setTermsChecked(!termsChecked)}
                    />
                    <label
                        htmlFor={'onfido-terms-checked'}
                        className="font-weight-bold checkbox-label"
                    >
                        I agree to the{' '}
                        <a
                            download={`Onfido Terms of Use.pdf`}
                            href={termsPdf}
                            target="_blank"
                            className="terms-and-conditions-link"
                        >
                            ID policy
                        </a>
                    </label>
                    <br />
                    <br />
                </Form>
            </div>
        </div>
    );
};

export default OnfidoBirthdayForm;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/signup/verification/OnfidoBirthdayForm.tsx